import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import {
  serializedToExportedString,
  type SerializedValue,
} from '../thread/ThreadMessage/useSerializeValue';
import type { Category } from '../types/api';

export type ValidationReturnType = MessageDescriptor | string | null;
export type ValidationFnType<T extends SerializedValue | unknown[] | unknown = unknown> = (
  elt: T,
) => ValidationReturnType | Promise<ValidationReturnType>;

export const validationMessages = defineMessages({
  email: {
    defaultMessage: 'Invalid email',
    id: 'ByuOj8',
  },
  minLength: {
    defaultMessage: 'too short',
    id: '9KObt1',
  },
  maxLength: {
    defaultMessage: 'too long',
    id: '1XpdlT',
  },
  identifier: {
    defaultMessage: 'The password must be between 4 and 30 characters long',
    id: 'erd1Ca',
  },
  password: {
    defaultMessage: 'The password must be at least 8 characters long',
    id: '8FWnqg',
  },
  domain: {
    defaultMessage: 'the provided domain is not valid',
    id: '8s1EI8',
  },
  required: {
    defaultMessage: 'this field is required',
    id: 't0xnkW',
  },
  minValidTag: {
    defaultMessage: 'Please choose at least 1 existing tag',
    id: 'a/HEgj',
  },
  maxValidTag: {
    defaultMessage: 'Please choose up to {maxValidTag} tags',
    id: 'HYBWHi',
  },
});

export const validateRequired: ValidationFnType = value =>
  value !== undefined ? null : validationMessages.required;

export const minLength: (min: number) => ValidationFnType<string | unknown[]> =
  (min: number) => value =>
    (value?.length ?? 0) >= min ? null : validationMessages.minLength;

export const visibleEditorValue = (value: SerializedValue | undefined) =>
  serializedToExportedString(value)?.replace(/(<p>)|(&nbsp;)|(<\/p>)|(\n)/gi, '');

export const requiredEditor = (value: string | undefined) => {
  const l = visibleEditorValue(value)?.length;
  return l != null && l > 0 ? null : validationMessages.required;
};
export const minEditorLength =
  (min = 1, errorMessage?: MessageDescriptor): ValidationFnType<string | undefined> =>
  value =>
    (visibleEditorValue(value)?.length ?? 0) >= min
      ? null
      : errorMessage || validationMessages.minLength;

export const maxLength: (max: number) => ValidationFnType<string | undefined | unknown[]> =
  (max: number) => value =>
    value == null || value.length <= max ? null : validationMessages.maxLength;

export const composeValidations =
  <T>(validations: ValidationFnType<T>[]): ValidationFnType<T> =>
  async (value: T) => {
    if (validations.length === 0) return null;

    const result = await validations[0](value);

    return result || composeValidations(validations.slice(1))(value);
  };

export const isEmail = (value: string) =>
  value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(value)
    ? validationMessages.email
    : null;

export const isValidCategory = (value: Category[], minTag = 1, maxTag = Infinity) => {
  if (value.length > maxTag) return validationMessages.maxValidTag;

  const isOnlyProposals = value.every(cat => cat.id?.startsWith('proposal'));
  if (value.length < minTag || isOnlyProposals) return validationMessages.minValidTag; // todo possibly add plural
  return null;
};

export const localhostRegex = /^(http:\/\/)?localhost:\d{4}$/;
export const domainRegex = /^(https?:\/\/)?([A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9]?\.)+[A-Za-z]{2,}$/;

/**
 * Réduit une saisie d'URL au HOST NU (B2_01) : strip du schéma, du port, et de tout ce qui
 * suit le host (path, slash final, query, hash). Sert à normaliser `website.url` AVANT
 * validation par `domainRegex`/`isDomain` — un site dont l'`url` stockée porte un `/` final
 * ou un chemin (`https://www.exemple.fr/salons/`) restait sinon invalide et bloquait l'édition
 * (bouton « Enregistrer » grisé). `domainRegex` n'est volontairement PAS assouplie (elle sert
 * aussi aux chips de `DomainsField`, qui ne doivent pas accepter de chemin) : on normalise en
 * amont. Le schéma final (toujours `https://`, décision B2_01) est re-préfixé par l'appelant.
 *
 * Exemples : `https://www.exemple.fr/salons/` → `www.exemple.fr` ; `http://x.fr:8080` → `x.fr` ;
 * `www.exemple.fr/` → `www.exemple.fr` ; `  x.fr  ` → `x.fr` ; `''` → `''`.
 */
export const normalizeHost = (input: string | undefined | null): string =>
  (input ?? '')
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '') // schéma (http://, https://, ftp://…)
    .replace(/[/?#].*$/, '') // path / slash final / query / hash
    .replace(/:\d+$/, ''); // port

export const isDomain = (domain: string) =>
  domainRegex.test(domain) ? null : validationMessages.domain;
